/**
 * SankeyGraph.tsx - Sankey flow diagram using D3
 * 
 * CHANGE HISTORY:
 * - 251105_1835 danielbeer: Implemented full Sankey diagram with D3 sankey layout
 * - 251031_2050 danielbeer: Initial creation
 * 
 * Flow-based visualization showing magnitude of connections.
 * Best for: Understanding data flow, resource allocation, process flows
 */

import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { sankey, sankeyLinkHorizontal, SankeyNode, SankeyLink } from 'd3-sankey';
import type { GraphNode, GraphEdge, GraphOptions, GraphCallbacks, GraphStyle, SankeyLayoutOptions } from './types';

export interface SankeyGraphProps<T = any> extends GraphOptions, GraphCallbacks<T> {
  data: {
    nodes: GraphNode<T>[];
    edges: GraphEdge[];
  };
  style?: GraphStyle;
  sankeyOptions?: SankeyLayoutOptions;
  className?: string;
}

interface SankeyNodeExtended extends SankeyNode<GraphNode, GraphEdge> {
  id: string;
  label: string;
}

interface SankeyLinkExtended extends SankeyLink<SankeyNodeExtended, GraphEdge> {
  value: number;
}

export const SankeyGraph = <T = any,>({
  data,
  width = 1200,
  height = 800,
  style = {},
  sankeyOptions: sankeyOptionsProp = {},
  onNodeClick,
  onNodeHover,
  onEdgeClick,
  onEdgeHover,
  className = '',
}: SankeyGraphProps<T>) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [hoveredLink, setHoveredLink] = useState<string | null>(null);

  useEffect(() => {
    if (!svgRef.current || data.nodes.length === 0) return;

    d3.select(svgRef.current).selectAll('*').remove();
    const svg = d3.select(svgRef.current);
    const sankeyOptions = { 
      nodeWidth: 15, 
      nodePadding: 20, 
      iterations: 32,
      ...sankeyOptionsProp 
    };

    const margin = { top: 20, right: 20, bottom: 20, left: 20 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    const container = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Create sankey layout
    const sankeyLayout = sankey<GraphNode, GraphEdge>()
      .nodeWidth(sankeyOptions.nodeWidth)
      .nodePadding(sankeyOptions.nodePadding)
      .extent([[0, 0], [innerWidth, innerHeight]])
      .iterations(sankeyOptions.iterations);

    // Transform data for sankey
    const nodeMap = new Map(data.nodes.map((n, i) => [n.id, i]));
    const sankeyData = {
      nodes: data.nodes.map(n => ({ ...n })),
      links: data.edges.map(e => ({
        ...e,
        source: nodeMap.get(e.source) ?? 0,
        target: nodeMap.get(e.target) ?? 0,
        value: e.weight ?? 1
      }))
    };

    // Compute layout
    const graph = sankeyLayout(sankeyData as any);

    // Draw links
    const links = container.append('g')
      .selectAll('path')
      .data(graph.links)
      .enter()
      .append('path')
      .attr('d', sankeyLinkHorizontal())
      .attr('stroke', (d: any) => {
        const edge = d as GraphEdge;
        if (typeof style.edgeColor === 'function') return style.edgeColor(edge);
        return edge.color || style.edgeColor || '#94a3b8';
      })
      .attr('stroke-width', (d: any) => Math.max(1, d.width))
      .attr('fill', 'none')
      .attr('opacity', (d: any) => hoveredLink === `${d.source.id}-${d.target.id}` ? 0.8 : 0.3)
      .style('cursor', 'pointer')
      .on('mouseover', function(event: any, d: any) {
        const linkId = `${d.source.id}-${d.target.id}`;
        setHoveredLink(linkId);
        if (onEdgeHover) {
          const edge = data.edges.find(e => e.source === d.source.id && e.target === d.target.id);
          if (edge) onEdgeHover(edge, event);
        }
      })
      .on('mouseout', function(event: any) {
        setHoveredLink(null);
        if (onEdgeHover) onEdgeHover(null, event);
      })
      .on('click', function(event: any, d: any) {
        if (onEdgeClick) {
          const edge = data.edges.find(e => e.source === d.source.id && e.target === d.target.id);
          if (edge) onEdgeClick(edge, event);
        }
      });

    // Add link labels for large flows
    container.append('g')
      .selectAll('text')
      .data(graph.links.filter((d: any) => d.width > 10))
      .enter()
      .append('text')
      .attr('x', (d: any) => (d.source.x1 + d.target.x0) / 2)
      .attr('y', (d: any) => (d.y0 + d.y1) / 2)
      .attr('dy', '0.35em')
      .attr('text-anchor', 'middle')
      .attr('font-size', '10px')
      .attr('fill', '#64748b')
      .text((d: any) => d.value);

    // Draw nodes
    const nodes = container.append('g')
      .selectAll('rect')
      .data(graph.nodes)
      .enter()
      .append('rect')
      .attr('x', (d: any) => d.x0)
      .attr('y', (d: any) => d.y0)
      .attr('height', (d: any) => d.y1 - d.y0)
      .attr('width', (d: any) => d.x1 - d.x0)
      .attr('fill', (d: any) => {
        const node = d as GraphNode;
        if (typeof style.nodeColor === 'function') return style.nodeColor(node);
        return node.color || style.nodeColor || '#3b82f6';
      })
      .attr('stroke', '#fff')
      .attr('stroke-width', 2)
      .attr('opacity', (d: any) => hoveredNode === d.id ? 1 : 0.8)
      .style('cursor', 'pointer')
      .on('mouseover', function(event: any, d: any) {
        setHoveredNode(d.id);
        if (onNodeHover) {
          const node = data.nodes.find(n => n.id === d.id);
          if (node) onNodeHover(node, event);
        }
      })
      .on('mouseout', function(event: any) {
        setHoveredNode(null);
        if (onNodeHover) onNodeHover(null, event);
      })
      .on('click', function(event: any, d: any) {
        if (onNodeClick) {
          const node = data.nodes.find(n => n.id === d.id);
          if (node) onNodeClick(node, event);
        }
      });

    // Add node labels
    container.append('g')
      .selectAll('text')
      .data(graph.nodes)
      .enter()
      .append('text')
      .attr('x', (d: any) => d.x0 < innerWidth / 2 ? d.x1 + 6 : d.x0 - 6)
      .attr('y', (d: any) => (d.y1 + d.y0) / 2)
      .attr('dy', '0.35em')
      .attr('text-anchor', (d: any) => d.x0 < innerWidth / 2 ? 'start' : 'end')
      .attr('font-size', '12px')
      .attr('fill', '#1e293b')
      .text((d: any) => d.label);

  }, [data, width, height, style, sankeyOptionsProp, hoveredNode, hoveredLink, onNodeClick, onNodeHover, onEdgeClick, onEdgeHover]);

  return (
    <svg
      ref={svgRef}
      width={width}
      height={height}
      className={`sankey-graph ${className}`}
      style={{
        border: '1px solid #ddd',
        borderRadius: '8px',
        background: style.backgroundColor || '#fafafa',
      }}
    />
  );
};

export default SankeyGraph;
